// src/tabs/beds24/MappingRow.jsx — Phase 6.A PR4 commit 8 (NIT-1 extraction)
//
// Composant row du tableau "Mappings actifs" dans Paramètres > Beds24.
// Extrait du parent Beds24SyncPanel.jsx pour passer sous 600 lignes
// (NIT-1 pessimist QA). Aucun changement fonctionnel : tests E2E PR4
// existants doivent passer SANS MODIFICATION (refactor cosmétique).
//
// DA (CHANTIER Paramètres) : habillage SEUL — pastilles statut sémantiques +
// icônes lucide (emoji→icône §8 ter). Labels « Éditer » / « Resync » CONSERVÉS
// (sélecteurs E2E) ; data-testid / data-sync-status / aria-label intacts.
//
// Props :
//   - mapping              { logement_id, beds24_property_id, beds24_room_id,
//                            active, updated_at }
//   - log                  { id, nom, prop } | null (résolu allLogs côté parent)
//   - config               logement_pricing_config row | null
//   - freshness            { freshness_label, fetched_at_max, ... } | null
//   - forcingResyncId      string | null (id en cours de Resync)
//   - fmtDate              helper formatage date
//   - onEdit({ existing, logement_id_default })
//   - onForceResync(logement_id)
//   - onDelete(logement_id)
//   - onReactivateMapping(mapping)
//   - styles               { th, td, btnSecondary } (tokens partagés)

import { useEffect, useRef } from "react";
import {
  Settings, RefreshCw, Trash2, RotateCcw, ArrowRightLeft, AlertTriangle,
  CircleSlash, Pencil, Pause, CheckCircle2, Lock,
} from "lucide-react";
import { DS } from "../../lib/ui";
import { StatusPill } from "../_paramsUi";
import { useMinStayDefaultSync } from "../../lib/use-min-stay-default";

export default function MappingRow({
  mapping: m,
  log,
  config,
  freshness,
  forcingResyncId,
  fmtDate,
  onEdit,
  onForceResync,
  onDelete,
  onReactivateMapping,
  onEditMinStay, // Phase 6.B PR #167c (ADR-101 Q-UI-2 E2) — ouvre MinStayDefaultEditModal
  onToggleMode, // Phase 6.C PR #167d (ADR-102, D-5) — ouvre PricingModeToggleModal
  toggleModeBusy, // Phase 6.C PR #167d (LPRd-4) — true si une bascule est en cours pour ce logement
  otherModalOpen, // Phase 6.C PR #167d (MPRd-2) — true si autre modal (editing / editingMinStay) ouverte
  manualPushSetCount, // Phase 6.C PR #167d (LPRd-5) — count daily_pricing.source='manual_push_set' (signal prod)
  styles,
}) {
  const { td, btnSecondary } = styles;
  // PR #167d LPRd-4 : disable row + opacity réduite si toggle en cours
  const rowStyle = {
    borderBottom: `1px solid ${DS.border}`,
    background: m.active ? DS.card : DS.pageBg,
    opacity: toggleModeBusy ? 0.5 : 1,
    transition: "opacity 150ms ease",
  };
  // PR #167d LPRd-5 : tooltip warning bascule si >50 dates manual_push_set
  const hasManyManualPushSet =
    typeof manualPushSetCount === "number" && manualPushSetCount > 50;
  // Tooltip différencié selon mode courant (D-5 + Q-1 wording)
  const toggleTooltip = !config
    ? "Configuration absente — non disponible"
    : config.pricing_mode === "manual"
      ? `Basculer en PriceLabs (TYPE A)${hasManyManualPushSet ? ` — ${manualPushSetCount} prix manuels actifs` : ""}`
      : `Repasser en mode manuel (TYPE B)${hasManyManualPushSet ? ` — ${manualPushSetCount} prix manuels actifs` : ""}`;
  return (
    <tr style={rowStyle}>
      <td style={td}>
        {log ? (
          `${log.nom} (${log.prop})`
        ) : (
          <em style={{ color: DS.danger }}>{m.logement_id} — orphelin</em>
        )}
      </td>
      <td style={td}>
        <code>{m.beds24_property_id}</code>
      </td>
      <td style={td}>
        <code>{m.beds24_room_id ?? "—"}</code>
      </td>
      <td style={td}>
        <ModeBadge mode={config?.pricing_mode} />
      </td>
      <td style={td}>
        <FreshnessCell freshness={freshness} />
      </td>
      <td style={td}>
        <MinStayDefaultCell
          config={config}
          mapping={m}
          onEditMinStay={onEditMinStay}
          styles={styles}
        />
      </td>
      <td style={td}>
        {m.active ? (
          <StatusPill tone="success"><CheckCircle2 size={11} strokeWidth={2} /> actif</StatusPill>
        ) : (
          <StatusPill tone="neutral"><Pause size={11} strokeWidth={2} /> désactivé</StatusPill>
        )}
      </td>
      <td style={td}>{fmtDate(m.updated_at)}</td>
      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
        <button
          onClick={() =>
            onEdit({
              existing: config,
              logement_id_default: m.logement_id,
            })
          }
          title={
            config
              ? "Éditer la config pricing (PriceLabs Listing ID / URL)"
              : "Configuration absente — utiliser bouton bascule pour changer de mode"
          }
          disabled={!config || otherModalOpen}
          style={{
            ...btnSecondary,
            opacity: !config || otherModalOpen ? 0.5 : 1,
            cursor: !config || otherModalOpen ? "not-allowed" : "pointer",
          }}
        >
          <Settings size={13} strokeWidth={1.9} /> Éditer
        </button>
        {config && (
          <>
            {/* `otherModalOpen` manquait ici (2026-09-28) : Éditer, bascule et
                supprimer s'éteignaient pendant qu'une modale est ouverte, pas
                Resync — on pouvait donc lancer le resync d'une AUTRE ligne
                par-dessus la garde ouverte pour un logement. */}
            <button
              onClick={() => onForceResync(m.logement_id)}
              disabled={
                forcingResyncId === m.logement_id ||
                toggleModeBusy ||
                otherModalOpen
              }
              title="Force resync prix Beds24 (rate-limit 5 min)"
              style={{
                ...btnSecondary,
                marginLeft: 6,
                opacity:
                  forcingResyncId === m.logement_id ||
                  toggleModeBusy ||
                  otherModalOpen
                    ? 0.5
                    : 1,
                cursor:
                  forcingResyncId === m.logement_id ||
                  toggleModeBusy ||
                  otherModalOpen
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <RefreshCw size={13} strokeWidth={1.9} /> Resync
            </button>
            {/* PR #167d (ADR-102, D-5) — bouton bascule mode (LPRd-2 ordre : avant suppression) */}
            <button
              onClick={() => onToggleMode?.(config)}
              disabled={toggleModeBusy || otherModalOpen}
              title={toggleTooltip}
              data-testid={`pricing-mode-toggle-${m.logement_id}`}
              style={{
                ...btnSecondary,
                marginLeft: 6,
                opacity: toggleModeBusy || otherModalOpen ? 0.5 : 1,
                cursor:
                  toggleModeBusy || otherModalOpen ? "not-allowed" : "pointer",
                ...(hasManyManualPushSet
                  ? {
                      borderColor: DS.warning,
                      color: DS.warningText,
                    }
                  : {}),
              }}
            >
              <ArrowRightLeft size={13} strokeWidth={1.9} />{" "}
              {toggleModeBusy
                ? "…"
                : config.pricing_mode === "manual"
                  ? "→ PriceLabs"
                  : "→ Manuel"}
              {hasManyManualPushSet ? (
                <AlertTriangle size={12} strokeWidth={2} style={{ marginLeft: 3 }} />
              ) : null}
            </button>
            {config?.active !== false && (
              <button
                onClick={() => onDelete(m.logement_id)}
                disabled={toggleModeBusy}
                title="Soft-delete (réversible, active=false)"
                aria-label="Supprimer la config pricing"
                style={{
                  ...btnSecondary,
                  marginLeft: 6,
                  color: DS.danger,
                  borderColor: "#f0c4c4",
                  opacity: toggleModeBusy ? 0.5 : 1,
                }}
              >
                <Trash2 size={13} strokeWidth={1.9} />
              </button>
            )}
          </>
        )}
        {/* BPR4-1 : bouton Réactiver si mapping inactif */}
        {!m.active && (
          <button
            onClick={() => onReactivateMapping(m)}
            title="Réactiver le mapping Beds24"
            style={{
              ...btnSecondary,
              marginLeft: 6,
              color: DS.marine,
              borderColor: DS.borderHover,
            }}
          >
            <RotateCcw size={13} strokeWidth={1.9} /> Réactiver
          </button>
        )}
      </td>
    </tr>
  );
}

// PR4 §3.4 : 3 badges Mode pricing (DS tokens validés CLAUDE.md)
function ModeBadge({ mode }) {
  if (!mode)
    return <StatusPill tone="warning"><AlertTriangle size={11} strokeWidth={2} /> non configuré</StatusPill>;
  if (mode === "pricelabs")
    return <StatusPill tone="info">PriceLabs</StatusPill>;
  if (mode === "manual")
    return <StatusPill tone="success">Manual</StatusPill>;
  return <span style={{ color: DS.textMuted }}>—</span>;
}

// PR #167c (ADR-101 Q-UI-2 E2) — cellule colonne 9 "Min nuits défaut".
// - TYPE A pricelabs : affichage read-only + badge "PriceLabs"
// - TYPE B manual : affichage valeur + bouton ✏️ qui ouvre MinStayDefaultEditModal
// - Mapping/config absent : "—"
//
// PR #167c amend C4 (§Auto-sync Vue 2) — auto-sync per-row au mount via
// useMinStayDefaultSync. Affiche un petit indicateur drift compact si
// Beds24 != DB (= status 'drift_corrected'). Pour TYPE A pricelabs, on
// désactive le sync (l'admin n'édite pas, pas besoin de drift indicator).
function MinStayDefaultCell({ config, mapping, onEditMinStay, styles }) {
  const { btnSecondary } = styles;

  // Hook auto-sync per-row — toujours appelé (Hooks rule) mais désactivé si
  // pas de config ou mapping absent (enabled=false).
  const syncEnabled =
    !!config &&
    config.pricing_mode === "manual" &&
    !!mapping &&
    !!mapping.active;
  const sync = useMinStayDefaultSync(config?.logement_id, syncEnabled);

  // PR #167c amend C10 (NQAc2-3 fix) — clearDrift() quand config.min_stay_default
  // change. Use case : user clique ✏️ → modal édition → save (push Beds24) →
  // modal ferme → refetchConfigs() Vue 2 met à jour la prop config. À ce point,
  // DB ↔ Beds24 sont alignés (le push a fait l'alignement). Le drift indicator
  // affiché avant le save n'est plus pertinent et doit disparaître.
  // Sans ce reset, firedRef.current reste true → state.beds24SyncStatus reste
  // 'drift_corrected' jusqu'à refresh complet de la page.
  const lastMinStayDefaultRef = useRef(config?.min_stay_default);
  useEffect(() => {
    if (!config) return;
    if (config.min_stay_default !== lastMinStayDefaultRef.current) {
      lastMinStayDefaultRef.current = config.min_stay_default;
      sync.clearDrift?.();
    }
  }, [config, sync]);

  // Pas de config → "—"
  if (!config) {
    return <span style={{ color: DS.textMuted }}>—</span>;
  }
  const value = config.min_stay_default;
  const valueText = value == null ? "—" : `min ${value}n`;

  // TYPE A pricelabs : read-only
  if (config.pricing_mode === "pricelabs") {
    return (
      <span
        style={{
          color: DS.catCielIco,
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
        title="Géré par PriceLabs (read-only)"
      >
        {valueText}
        <Lock size={11} strokeWidth={1.9} style={{ color: DS.textMuted }} />
      </span>
    );
  }

  // PR #167c amend C4 — détection drift per-ligne pour TYPE B manual.
  // drift_corrected = Beds24 != DB → DB UPSERT effectué silencieusement.
  // L'admin voit l'icône amber, hover tooltip donne le détail Beds24=X / DB=Y.
  const driftCorrected = sync.beds24SyncStatus === "drift_corrected";
  const syncFailed = sync.beds24SyncStatus === "failed";

  // TYPE B manual : valeur + bouton ✏️ + indicateur drift éventuel
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{ color: value == null ? DS.textMuted : DS.marine, fontWeight: 600 }}
      >
        {valueText}
      </span>
      {driftCorrected && (
        <span
          data-testid={`min-stay-drift-indicator-${config.logement_id}`}
          data-sync-status="drift_corrected"
          title={`Auto-sync : Beds24 a été détecté différent de la DB FBZ et synchronisé. Beds24 brut = ${sync.beds24Raw == null ? "null" : sync.beds24Raw} · DB après sync = ${sync.dbValueAfter == null ? "null" : sync.dbValueAfter} · ${sync.fetchedAt || ""}`}
          style={{
            color: DS.warningText,
            display: "inline-flex",
            padding: "1px 6px",
            background: DS.warningBg,
            border: `0.5px solid #f1d9ac`,
            borderRadius: 9,
          }}
          aria-label="Drift Beds24 détecté et corrigé silencieusement"
        >
          <AlertTriangle size={11} strokeWidth={2} />
        </span>
      )}
      {syncFailed && (
        <span
          data-testid={`min-stay-drift-indicator-${config.logement_id}`}
          data-sync-status="failed"
          title="Auto-sync Beds24 échouée — valeur affichée = cache DB FBZ"
          style={{
            color: DS.textMuted,
            display: "inline-flex",
          }}
          aria-label="Sync Beds24 échouée, valeur cache DB"
        >
          <CircleSlash size={11} strokeWidth={2} />
        </span>
      )}
      <button
        onClick={() => onEditMinStay?.(config.logement_id)}
        disabled={!mapping || !mapping.active}
        title="Éditer min_stay_default"
        aria-label="Éditer min_stay_default"
        style={{
          ...btnSecondary,
          padding: "3px 8px",
          opacity: !mapping || !mapping.active ? 0.5 : 1,
        }}
      >
        <Pencil size={12} strokeWidth={1.9} />
      </button>
    </span>
  );
}

// PR4 §3.5 : freshness label + fetched_at_max formaté (couleurs DS tokens)
function FreshnessCell({ freshness }) {
  if (
    !freshness ||
    freshness.freshness_label === "no_data" ||
    !freshness.fetched_at_max
  ) {
    return <span style={{ color: DS.textMuted }}>—</span>;
  }
  const tones = {
    fresh: "success", // vert < 1h
    ok: "info", // bleu < 6h
    stale: "warning", // ambre < 24h
    very_stale: "danger", // rouge ≥ 24h
  };
  const labels = {
    fresh: "< 1h",
    ok: "< 6h",
    stale: "< 24h",
    very_stale: "≥ 24h",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
      <StatusPill tone={tones[freshness.freshness_label] || "neutral"}>
        {labels[freshness.freshness_label] || freshness.freshness_label}
      </StatusPill>
      <span style={{ fontSize: 10, color: DS.textMuted }}>
        {(() => {
          const d = new Date(freshness.fetched_at_max)
          return Number.isNaN(d.getTime())
            ? "—"
            : d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
        })()}
      </span>
    </div>
  );
}
